import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import NextLink from 'next/link';
import Pagination from '@mui/material/Pagination';
import { Box, Button, Container, Divider, Link } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { makeStyles } from '@mui/styles';
import BaseLayout from 'layouts/BaseLayout';
import { useCreations } from 'contexts/useCreations';
import useStripeSubscription from 'hooks/useStripeSubscription';
import CreationService from 'services/CreationService';
import ShapesBox from 'components/MyCreations/ShapesBox';
import Filters from 'components/MyCreations/Filters';
import FoldersAccordion from 'components/MyCreations/FoldersAccordion';
import CreationsAccordion from 'components/MyCreations/CreationsAccordion';
import CreationsList from 'components/MyCreations/CreationsList';
import FriendsReferralBanner from 'components/Invite/FriendsReferralBanner';
import AlertAlt from 'components/AlertAlt';
import { COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import useIsMobile from 'hooks/useIsMobile';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { withAuth } from 'features/auth/withAuth';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { useJournal } from 'hooks/UseJournalFeatures';
import { UserTypeEnum } from 'enums';
import JournalService from 'services/JournalService';
import { useRouter } from 'next/router';

export const useStyles = makeStyles((theme) => ({
  headerBox: {
    padding: theme.spacing(3, 0),
    backgroundColor: '#f9f9f9',
  },
  header: {
    fontSize: '1.75rem',
  },
  newCreationButton: {
    backgroundColor: '#D6246E',
    color: theme.palette.common.white,
    borderRadius: '40px',
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    '&:hover': {
      filter: 'brightness(0.9)',
      backgroundColor: '#D6246E',
    },
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  greyText: {
    color: theme.palette.grey[700],
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),

    '& li:not(:first-child, :last-child) button': {
      color: '#5F29CC',
    },

    '& li button.Mui-selected, & li button.Mui-selected:hover': {
      color: `${theme.palette.common.white} !important`,
      backgroundColor: '#5F29CC',
    },
  },
  alert: {
    justifyContent: 'center',
  },
}));

const initialCreationsData = {
  page: 1,
  perPage: 16,
  total: 0,
  data: [],
};

function MyCreationsPage() {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { user, userJournalSettings } = useAuthUser();
  const [allCreations, refreshAllCreations] = useCreations();
  const currentStripeSubscription = useStripeSubscription();
  const router = useRouter();
  const [creationsPage, setCreationsPage] = useState(1);
  const [activeFolderCreationsPage, setActiveFolderCreationsPage] = useState(1);
  const [filters, setFilters] = useState({ status: 'available', order: 'last_modified' });
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState();
  const [activeFolderCreations, setActiveFolderCreations] = useState({ ...initialCreationsData });
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [newCreationModalOpen, setNewCreationModalOpen] = useState(false);
  const [latestInvoice, setLatestInvoice] = useState(null);
  const isInvoiceOpen = latestInvoice?.status === 'open' && latestInvoice?.attempt_count === 0;
  const isInvoiceError =
    latestInvoice?.status === 'unpaid' ||
    (latestInvoice?.attempt_count > 0 && latestInvoice?.status !== 'paid');
  const { canAccessFeature } = useJournal();
  const disableNewCreation =
    user.tp_user === UserTypeEnum.JOURNAL &&
    allCreations.length >= userJournalSettings.creationsAvailable;

  useEffect(() => {
    const { journal_id, publishers, mode } = router.query;
    if (journal_id && publishers === 'true') {
      JournalService.updateUserJournal(journal_id)
        .then(() => {
          console.log('User journal updated');
        })
        .catch((error) => {
          console.error('Error updating user journal:', error);
        });
    }
  }, [router.query]);

  async function fetchFolders() {
    try {
      const response = await CreationService.findAllFolders();

      setFolders(response.data);
    } catch (e) {
      console.error(e);
    }
  }

  const { data: creations = initialCreationsData, refetch: refetchUserCreations } = useQuery({
    queryKey: ['creations', filters.status, filters.order, creationsPage],
    queryFn: async () => {
      const fetchFilters = makeCreationsFetchFilters({
        folderId: '-1',
        page: creationsPage,
        perPage: initialCreationsData.perPage,
      });

      const response = await CreationService.findAll(fetchFilters);

      return response.data;
    },
  });

  async function fetchActiveFolderCreations(folder, paginationPage) {
    try {
      const response = await CreationService.findAll(
        makeCreationsFetchFilters({
          folderId: folder.id,
          page: paginationPage,
          perPage: initialCreationsData.perPage,
        }),
      );

      setActiveFolderCreations(response.data);
    } catch (e) {
      console.error(e);
    }
  }

  function makeCreationsFetchFilters(customFilters = {}) {
    let orderField;
    let orderDirection;

    if (filters.order === 'last_modified') {
      orderField = 'edited';
      orderDirection = 'DESC';
    } else if (filters.order === 'a_to_z') {
      orderField = 'name';
      orderDirection = 'ASC';
    }

    return {
      ...customFilters,
      status: filters.status,
      orderField,
      orderDirection,
    };
  }

  function handleFolderEnter(folder) {
    setActiveFolder(folder);
    fetchActiveFolderCreations(folder, activeFolderCreationsPage);
  }

  function handleFolderOut() {
    setActiveFolder();
    setActiveFolderCreations({ ...initialCreationsData });
    setActiveFolderCreationsPage(1);
  }

  function handleFolderOptionClick(option) {
    if (option.key === 'rename') {
      setFolderModalOpen(true);
    }
  }

  async function handleFolderSave(name, folder) {
    try {
      if (!folder) {
        await CreationService.createFolder({ name });
      } else {
        await CreationService.updateFolder(folder.id, { name });
      }
    } catch (e) {
      console.error(e);
    } finally {
      fetchFolders();
      setFolderModalOpen(false);
    }
  }

  async function handleFolderDelete(folder) {
    try {
      await CreationService.deleteFolder(folder.id);
    } catch (e) {
      console.error(e);
    } finally {
      fetchFolders();
      setCreationsPage(creationsPage);
      refetchUserCreations();
    }
  }

  function handleCreationsRefresh() {
    const pageHasSingleCreation = creations.data.length === 1;

    if (pageHasSingleCreation && creationsPage > 1) {
      const previousPage = creationsPage - 1;

      setCreationsPage(previousPage);
      refetchUserCreations();
    } else {
      setCreationsPage(creationsPage);
      refetchUserCreations();
    }
  }

  function handleActiveFolderCreationsRefresh() {
    const pageHasSingleCreation = activeFolderCreations.data.length === 1;

    if (pageHasSingleCreation && activeFolderCreationsPage > 1) {
      const previousPage = activeFolderCreationsPage - 1;

      setActiveFolderCreationsPage(previousPage);
      fetchActiveFolderCreations(activeFolder, previousPage);
    } else {
      fetchActiveFolderCreations(activeFolder, activeFolderCreationsPage);
    }
  }

  async function handleCreationOptionClick(option, creation) {
    switch (option.key) {
      case 'restore':
        await handleCreationRestore(creation);
        break;
      case 'remove_from_folder':
        await handleCreationUpdate(null, creation);
        break;
      case 'delete_sharing':
        await handleCreationDeleteSharing(creation);
        break;
    }
  }

  async function handleCreationUpdate(folderId, creation) {
    try {
      await CreationService.update(creation.id, { folderId });
    } catch (e) {
      console.error(e);
    } finally {
      handleCreationsRefresh();

      if (activeFolder) {
        handleActiveFolderCreationsRefresh();
      }

      queryClient.invalidateQueries('creations');
    }
  }

  async function handleCreationDelete(creation) {
    try {
      await CreationService.delete(creation.id);
    } catch (e) {
      console.error(e);
    } finally {
      handleCreationsRefresh();

      refreshAllCreations();

      if (activeFolder) {
        handleActiveFolderCreationsRefresh();
      }

      queryClient.invalidateQueries('creations');
    }
  }

  async function handleCreationRestore(creation) {
    try {
      await CreationService.restore(creation.id);
    } catch (e) {
      console.error(e);
    } finally {
      handleCreationsRefresh();

      refreshAllCreations();

      if (activeFolder) {
        handleActiveFolderCreationsRefresh();
      }

      queryClient.invalidateQueries('creations');
    }
  }

  async function handleCreationDeleteSharing(creation) {
    try {
      await CreationService.deleteSharing(creation.id);
    } catch (e) {
      console.error(e);
    } finally {
      handleCreationsRefresh();
    }
  }

  async function handleCreationsPaginationChange(_, newPage) {
    if (newPage !== creationsPage) {
      setCreationsPage(newPage);

      await refetchUserCreations();
    }
  }

  async function handleActiveFolderCreationsPaginationChange(_, newPage) {
    if (newPage !== activeFolderCreationsPage) {
      setActiveFolderCreationsPage(newPage);

      await fetchActiveFolderCreations(activeFolder, newPage);
    }
  }

  useEffect(() => {
    if (filters.status === 'shared') {
      setFolders([]);
      setActiveFolder();
      setActiveFolderCreationsPage(1);
      setActiveFolderCreations({ ...initialCreationsData });

      setCreationsPage(1);

      return;
    }

    setCreationsPage(1);

    fetchFolders();

    if (activeFolder) {
      setActiveFolderCreationsPage(1);

      fetchActiveFolderCreations(activeFolder, 1);
    }
  }, [filters]);

  useEffect(() => {
    setLatestInvoice(currentStripeSubscription?.latest_invoice);
  }, [currentStripeSubscription]);

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <Head>
        <title>My Creations - Mind The Graph</title>
      </Head>

      <div className={classes.headerBox}>
        <Container maxWidth="md" disableGutters={true}>
          {isInvoiceError && (
            <Box mb={3}>
              <AlertAlt className={classes.alert} severity="error">
                Alert: Credit card update required,{' '}
                <NextLink href="/my-account/billing/update-card" passHref={true}>
                  <Link underline="hover">click to update</Link>
                </NextLink>
                .
              </AlertAlt>
            </Box>
          )}

          {isInvoiceOpen && (
            <Box mb={3}>
              <AlertAlt className={classes.alert} severity="warning">
                You have an open invoice.{' '}
                <NextLink href="/my-account/billing/update-card" passHref={true}>
                  <Link underline="hover">Click here</Link>
                </NextLink>{' '}
                to confirm the subscription.
              </AlertAlt>
            </Box>
          )}

          {/* {canAccessFeature('MyCreations', 'banner') && (
            <Box pt={3}>
              <BannerMyCreations />
            </Box>
          )} */}

          <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
            <h1 className={classes.header}>My Creations</h1>

            <Button
              variant="contained"
              className={classes.newCreationButton}
              startIcon={<AddIcon />}
              onClick={() => setNewCreationModalOpen(true)}
              disabled={disableNewCreation}
            >
              New Creation
            </Button>
          </Box>
        </Container>
      </div>

      <Container maxWidth="md" disableGutters={true}>
        <Box mt={5} pb={5}>
          <Box mt={4}>
            <Filters
              filters={filters}
              onChange={(newFilters) => setFilters(newFilters)}
              onCreateFolderClick={() => setFolderModalOpen(true)}
            />
          </Box>

          <Box mt={4}>
            <FoldersAccordion
              folders={folders}
              activeFolder={activeFolder}
              creationsListComponent={
                activeFolder && (
                  <>
                    <CreationsList
                      creations={activeFolderCreations.data}
                      onOptionClick={handleCreationOptionClick}
                      onModalActionClick={handleCreationUpdate}
                      onDeleteModalActionClick={handleCreationDelete}
                    />

                    {activeFolderCreations.total > initialCreationsData.perPage && (
                      <Pagination
                        className={classes.pagination}
                        page={activeFolderCreationsPage}
                        count={Math.ceil(
                          activeFolderCreations.total / initialCreationsData.perPage,
                        )}
                        onChange={handleActiveFolderCreationsPaginationChange}
                      />
                    )}
                  </>
                )
              }
              onEnter={handleFolderEnter}
              onOut={handleFolderOut}
              onOptionClick={handleFolderOptionClick}
              modalOpen={folderModalOpen}
              onModalClose={() => setFolderModalOpen(false)}
              onModalActionClick={handleFolderSave}
              onDeleteModalActionClick={handleFolderDelete}
            />

            {!!folders.length && <Divider className={classes.divider} />}

            <CreationsAccordion
              creations={creations.data}
              shared={filters.status === 'shared'}
              addToFolderOption={!!folders.length}
              onOptionClick={handleCreationOptionClick}
              onModalActionClick={handleCreationUpdate}
              onDeleteModalActionClick={handleCreationDelete}
            >
              {creations.total > initialCreationsData.perPage && (
                <Pagination
                  className={classes.pagination}
                  page={creationsPage}
                  count={Math.ceil(creations.total / initialCreationsData.perPage)}
                  onChange={handleCreationsPaginationChange}
                />
              )}
            </CreationsAccordion>
          </Box>

          {canAccessFeature('MyCreations', 'banner') && (
            <Box mt={4}>{/* <FriendsReferralBanner /> */}</Box>
          )}
        </Box>
      </Container>

      <ShapesBox isOpen={newCreationModalOpen} onClose={() => setNewCreationModalOpen(false)} />
    </BaseLayout>
  );
}

export default withAuth(MyCreationsPage);
